import { useState, useEffect, useCallback } from 'react';
import { apiService, apiHelpers } from '../services/api';

// Hook for managing client selection and client list
export const useClients = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getClients();
      if (response.success) {
        setClients(response.clients || []);
      } else {
        setError('Failed to fetch clients');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchClients = useCallback(async (query) => {
    // Require minimum 3 characters for search
    if (!query || query.length < 3) {
      setClients([]);
      return [];
    }

    setSearching(true);
    setError(null);

    try {
      const response = await apiService.searchClients(query);
      if (response.success) {
        setClients(response.clients || []);
        return response.clients || [];
      } else {
        setError('Failed to search clients');
        return [];
      }
    } catch (err) {
      setError(err.message || 'Failed to search clients');
      setClients([]);
      return [];
    } finally {
      setSearching(false);
    }
  }, []);

  const createClient = useCallback(async (clientId) => {
    // For now, we'll just add it to the local list
    // The actual client creation happens when first document is uploaded
    if (clientId && !clients.includes(clientId)) {
      setClients(prev => [...prev, clientId].sort());
      setSelectedClient(clientId);
    }
  }, [clients]);

  return {
    clients,
    selectedClient,
    setSelectedClient,
    createClient,
    loading,
    searching,
    error,
    refetch: fetchClients,
    searchClients,
  };
};

// Hook for managing documents for a client
export const useDocuments = (clientId) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!clientId) {
      setDocuments([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getClientDocuments(clientId);
      if (response.success) {
        setDocuments(response.documents || []);
      } else {
        setError('Failed to fetch documents');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const deleteDocument = useCallback(async (documentId) => {
    if (!clientId || !documentId) return;

    try {
      const response = await apiService.deleteDocument(clientId, documentId);
      if (response.success) {
        setDocuments(prev => prev.filter(doc => doc.document_id !== documentId));
        return true;
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete document');
      return false;
    }
  }, [clientId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refetch: fetchDocuments,
    deleteDocument,
  };
};

// Hook for document upload functionality
export const useDocumentUpload = (clientId, onUploadComplete) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);

  const uploadDocument = useCallback(async (file, metadata = {}) => {
    if (!clientId || !file) {
      setError('Missing client ID or file');
      return false;
    }

    setUploading(true);
    setProgress(0);
    setError(null);
    setCurrentJob(null);

    try {
      // Upload and wait for completion
      const result = await apiHelpers.uploadAndWaitForCompletion(
        clientId,
        file,
        metadata,
        (progressPercent) => {
          setProgress(progressPercent);
        },
        (statusUpdate) => {
          setCurrentJob(statusUpdate);
        }
      );

      if (result.status === 'completed') {
        setProgress(100);
        if (onUploadComplete) {
          onUploadComplete(result);
        }
        return true;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      return false;
    } finally {
      setUploading(false);
      // Reset state immediately to prevent race conditions on rapid re-uploads
      // Only keep visual feedback briefly, not the actual state
      setTimeout(() => {
        setProgress(0);
        setCurrentJob(null);
        // Don't clear error immediately - let user see it
        // setError(null);
      }, 2000);
    }
  }, [clientId, onUploadComplete]);

  return {
    uploading,
    progress,
    error,
    currentJob,
    uploadDocument,
  };
};

// Hook for chat/Q&A functionality
export const useChat = (clientId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(async (query, timestampFilters = {}) => {
    if (!clientId || !query.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date(),
      timestampFilters: timestampFilters, // Store filters for reference
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      // Pass timestamp filters to the API
      const response = await apiService.queryDocuments(clientId, query, timestampFilters);

      if (response.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: response.answer,
          timestamp: new Date(),
          sourceDocuments: response.source_documents,
          confidence: response.confidence,
          processingTime: response.processing_time_ms,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(response.error || 'Query failed');
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}`,
        timestamp: new Date(),
        isError: true,
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(err.message || 'Query failed');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  // Clear messages when client changes
  useEffect(() => {
    clearMessages();
  }, [clientId, clearMessages]);

  return {
    messages,
    loading,
    error,
    sendMessage,
    clearMessages,
  };
};

// Hook for PDF viewing functionality
export const usePdfViewer = () => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPdf = useCallback(async (clientId, documentId) => {
    if (!clientId || !documentId) return;

    setLoading(true);
    setError(null);

    try {
      // Get presigned URL from S3 (direct browser access, no blob needed)
      const presignedUrl = await apiHelpers.getPdfPresignedUrl(clientId, documentId);
      if (presignedUrl) {
        setPdfUrl(presignedUrl);
        setCurrentPage(1);
      } else {
        throw new Error('Failed to load PDF');
      }
    } catch (err) {
      setError(err.message || 'Failed to load PDF');
    } finally {
      setLoading(false);
    }
  }, []);

  const goToPage = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  }, [totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  // No cleanup needed for presigned URLs (they're just regular URLs, not blob URLs)

  return {
    pdfUrl,
    currentPage,
    totalPages,
    loading,
    error,
    loadPdf,
    goToPage,
    nextPage,
    prevPage,
    setTotalPages,
  };
};

// Hook for system status
export const useSystemStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [healthResponse, statsResponse] = await Promise.all([
        apiService.checkHealth(),
        apiService.getSystemStats(),
      ]);

      setStatus({
        health: healthResponse,
        stats: statsResponse,
      });
    } catch (err) {
      setError(err.message || 'Failed to check system status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  return {
    status,
    loading,
    error,
    refetch: checkStatus,
  };
};